import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { Typography, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ThumbUp from "@mui/icons-material/ThumbUp";
import ThumbDown from "@mui/icons-material/ThumbDown";
import ColorIcon from "@mui/icons-material/Palette";
import TagIcon from "@mui/icons-material/LocalOffer";
import NextIcon from "@mui/icons-material/ArrowForward";
import PrevIcon from "@mui/icons-material/ArrowBack";
import Tags from "../Tags";
import ColorSelect from "../ColorSelect";
import { ENABLE_IPTC_UI } from "../../config/features";
import {
  dialogPaperStyle,
  dialogTitleSx,
  imageTitleSx,
  titleActionsSx,
  floatingActionButtonSx,
  tagsContainerSx,
  centeredFlexSx,
  rightAlignedSx,
  dialogActionsSx,
  previewContainerSx,
  previewImageSx,
  prevNavButtonSx,
  nextNavButtonSx,
} from "./styles";

function PreviewImageView({ image, total, meta, onPrev, onNext }) {
  const showNavigation = total > 1;

  return (
    <Grid item xs={12} md={meta ? 6 : 12}>
      <Box sx={previewContainerSx}>
        <Grid
          className="open-image"
          crossOrigin="anonymous"
          style={previewImageSx(image)}
        />
        {showNavigation ? (
          <React.Fragment>
            <IconButton
              disableRipple
              disableFocusRipple
              disableTouchRipple
              onClick={onPrev}
              sx={prevNavButtonSx}
            >
              <PrevIcon />
            </IconButton>
            <IconButton
              disableRipple
              disableFocusRipple
              disableTouchRipple
              onClick={onNext}
              sx={nextNavButtonSx}
            >
              <NextIcon />
            </IconButton>
          </React.Fragment>
        ) : null}
      </Box>
    </Grid>
  );
}

export default function PreviewView({ viewModel }) {
  if (viewModel.isDeleted) {
    return null;
  }

  return (
    <React.Fragment>
      <Dialog
        fullWidth={viewModel.fullWidth}
        maxWidth={viewModel.maxWidth}
        open={viewModel.open}
        onClose={viewModel.handleClose}
        PaperProps={{
          style: dialogPaperStyle,
        }}
      >
        <DialogTitle sx={dialogTitleSx}>
          <Typography color="white" sx={imageTitleSx}>
            {viewModel.localImage.image.title}
          </Typography>
          <Box sx={titleActionsSx}>
            {!viewModel.isBlockMode ? (
              <IconButton
                disableRipple
                disableFocusRipple
                disableTouchRipple
                sx={{ ...floatingActionButtonSx, mr: 1 }}
                component="a"
                onClick={() => {
                  viewModel.saveFile(
                    viewModel.localImage.image.data.imageURL.replace(/\+/g, "%2B")
                  );
                }}
              >
                <DownloadIcon />
              </IconButton>
            ) : null}
            <IconButton
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={floatingActionButtonSx}
              onClick={viewModel.handleClose}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid
            container
            className="image-holder"
            spacing={ENABLE_IPTC_UI && viewModel.isMetaOpen ? 2 : 0}
          >
            <PreviewImageView
              image={viewModel.imageUrl}
              total={viewModel.localImage.total}
              meta={ENABLE_IPTC_UI && viewModel.isMetaOpen}
              onPrev={viewModel.onPrev}
              onNext={viewModel.onNext}
            />

            {ENABLE_IPTC_UI && viewModel.isMetaOpen ? (
              <Grid item xs={12} md={6} sx={tagsContainerSx}>
                <Tags
                  saved={viewModel.saved}
                  setSaved={viewModel.setSaved}
                  tagSelected={(tag, id) => {
                    viewModel.tagSelected(tag, id);
                    viewModel.setSaved(true);
                  }}
                  folderInfo={viewModel.folderInfo}
                />
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Grid container>
            <Grid item xs={12} sx={centeredFlexSx}>
              <Grid container>
                <Grid item xs={6}>
                  {!viewModel.isViewerMode ? (
                    <IconButton
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      onClick={() =>
                        viewModel.handleToggleApprove(viewModel.getDownvoteAction())
                      }
                      variant="contained"
                      size="small"
                      color={viewModel.approved === false ? "error" : "button"}
                    >
                      <ThumbDown />
                    </IconButton>
                  ) : null}
                </Grid>

                <Grid item xs={6} sx={rightAlignedSx}>
                  {!viewModel.isViewerMode ? (
                    <IconButton
                      disableRipple
                      disableFocusRipple
                      disableTouchRipple
                      onClick={() =>
                        viewModel.handleToggleApprove(
                          !viewModel.approved ? true : "reset"
                        )
                      }
                      variant="contained"
                      size="small"
                      color={viewModel.approved === true ? "success" : "button"}
                    >
                      <ThumbUp />
                    </IconButton>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
            <Grid xs={12} item container>
              <Grid xs={12} item sx={centeredFlexSx}>
                <IconButton
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  onClick={viewModel.handleDeleteImage}
                  variant="contained"
                  size="small"
                  color="button"
                  className={viewModel.classes.button}
                >
                  <DeleteIcon />
                </IconButton>
                <IconButton
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  onClick={viewModel.handleOpenColorDropdown}
                  variant="contained"
                  size="small"
                  color="button"
                  className={viewModel.classes.button}
                >
                  <ColorIcon />
                </IconButton>
                <ColorSelect
                  anchorEl={viewModel.colorAnchorEl}
                  open={viewModel.isColorDropdownOpen}
                  initialColor={viewModel.localImage.image.color || "none"}
                  onClose={viewModel.handleCloseColorDropdown}
                  onSave={viewModel.handleSaveColor}
                />
                {ENABLE_IPTC_UI ? (
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    onClick={viewModel.handleToggleMeta}
                    variant="contained"
                    size="small"
                    color={viewModel.isMetaOpen ? "highlight" : "button"}
                    className={viewModel.classes.button}
                  >
                    <TagIcon />
                  </IconButton>
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
